import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Header from '../components/header/Header';
import ModernPhotoGallery from '../components/Gallery/ModernPhotoGallery';
import PageSEO from '../components/PageSEO';
import PriceCard from '../components/priceCard/PriceCard';
import { priceImages } from '../helpers/priceList';
import GallerySlider from '../components/gallerySlider/GallerySlider';
import BookingModal from '../components/BookingModal/BookingModal';
import { FAQSection } from '../components/MarketingComponents/FAQSection';
import '../components/button/style.css';
import './../style/gallery.css';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const Gallery = () => {
  const { t } = useTranslation('gallery');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const headerText = t('header.text');
  const seoDesc = headerText.length > 160 ? headerText.slice(0, 157) + '…' : headerText;

  const priceItems = t('prices.items', { returnObjects: true }).map((item, i) => ({
    ...item,
    img: priceImages[i],
  }));

  const openBooking = (packageType = null) => {
    setSelectedPackage(packageType);
    setIsBookingModalOpen(true);
  };

  const closeBooking = () => {
    setIsBookingModalOpen(false);
    setSelectedPackage(null);
  };

  return (
    <>
      <PageSEO
        title={`${t('header.title')} ${t('header.subtitle')} | PixelCraft`}
        description={seoDesc}
        path="/gallery"
      />
      <Header
        title={t('header.title')}
        subtitle={t('header.subtitle')}
        subtitle_2={t('header.subtitle2')}
        text={t('header.text')}
        typedTextStrings={t('header.typedStrings', { returnObjects: true })}
        link="#gallery"
        button_text={t('header.buttonText')}
      />

      <main>

        {/* ---- Photo Portfolio ---- */}
        <section className="section" id="gallery">
          <div className="container">
            <ModernPhotoGallery />
          </div>
        </section>

        {/* ---- Pricing ---- */}
        <section className="section" id="price">
          <div className="container">
            <h2 className="title-2">
              {t('pricing.title')} <span className="accent">{t('pricing.titleAccent')}</span>
            </h2>
            <p className="gallery__subtitle">{t('pricing.subtitle')}</p>

            <div className="prices__cards-inner">
              {priceItems.map((price, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: '-60px' }}
                  custom={index}
                >
                  <PriceCard
                    img={price.img}
                    title={price.title}
                    price={price.price}
                    descriptions={price.descriptions}
                    onClick={() => openBooking(price.title)}
                  />
                </motion.div>
              ))}
            </div>

            <p className="gallery__vat-note">{t('pricing.vatNote')}</p>
          </div>
        </section>

        {/* ---- FAQ ---- */}
        <FAQSection />

        {/* ---- Book CTA ---- */}
        <section className="section">
          <div className="container">
            <motion.div
              className="gallery__cta-wrap"
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <h2 className="gallery__cta-title">
                {t('cta.title')} <span className="accent">{t('cta.titleAccent')}</span>
              </h2>
              <p className="gallery__cta-text">{t('cta.desc')}</p>
              <button className="button" onClick={() => openBooking()}>
                {t('cta.bookBtn')}
              </button>
            </motion.div>
          </div>
        </section>

        {/* ---- Gallery Slider (nature & behind-the-scenes) ---- */}
        <section className="section gallery__slider-section">
          <GallerySlider />
        </section>

      </main>

      {isBookingModalOpen && (
        <BookingModal
          selectedPackage={selectedPackage}
          onClose={closeBooking}
          onBookingComplete={closeBooking}
        />
      )}
    </>
  );
};

export default Gallery;